import type { IconProps } from '@phosphor-icons/react';
import type { FC, ReactElement } from 'react';

import { BagSimpleIcon, BagIcon } from '@phosphor-icons/react';
import styled, { useTheme, css } from 'styled-components';

import { getCartAriaLabel } from './utilities/getCartAriaLabel';
import { FesLink } from '../../core/link/FesLink';
import { Typography } from '../../core/typography/Typography';
import { HeaderButton } from '../HeaderStyle';
import { useCartContext } from '~/utilities/context/dynamic/CartContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

const S = {
  ShoppingCartButton: styled(FesLink)`
    ${HeaderButton}
  `,
  Amount: styled(Typography)`
    ${({ theme }) => css`
      ${getSiteStyles(theme.siteType, {
        [SiteType.Aaf]: css`
          font-weight: 400;
        `,
      })}
    `}
  `,
};

const BagIconBold = ({ color, size }: IconProps) => (
  <BagIcon color={color} size={size} weight="bold" />
);

const MiniCartIconMap: Record<SiteType, FC<IconProps>> = {
  [SiteType.Aaf]: BagIcon,
  [SiteType.Employeeshop]: BagIconBold,
  [SiteType.Gstar]: BagIconBold,
  [SiteType.Outlet]: BagIconBold,
  [SiteType.Wahts]: BagSimpleIcon,
};

export const ShoppingCartButton = (): ReactElement => {
  const { miniCart, content } = useCartContext();
  const theme = useTheme();
  const cartAmount = miniCart.totalItems ?? 0;
  const { siteType } = useAppContext();
  const MiniCartIcon = MiniCartIconMap[siteType];

  const { cartAriaLabel } = getCartAriaLabel({ content, amount: cartAmount });

  return (
    <S.ShoppingCartButton
      href="/checkout/shopping-bag"
      aria-label={cartAriaLabel}
      data-cs-capture=""
      data-testid="mini-cart-icon"
    >
      <MiniCartIcon aria-hidden="true" size={theme.components.icon.topNavigation.size} />

      {cartAmount > 0 && (
        <S.Amount
          aria-hidden="true"
          component="span"
          variant="title"
          testId="mini-cart-count"
          data-cs-capture=""
        >
          {cartAmount}
        </S.Amount>
      )}
    </S.ShoppingCartButton>
  );
};
